/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Developer API Endpoint Usage Bar Chart Component (Task 10.6.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React from 'react';
import { Layers } from 'lucide-react';
import { useTranslation } from '../../../i18n';
import { useDeveloperAnalyticsStore } from '../../../stores/useDeveloperAnalyticsStore';

export const EndpointUsageBarChart: React.FC = () => {
  const { t } = useTranslation();
  const { getEndpointBreakdown } = useDeveloperAnalyticsStore();

  const endpoints = getEndpointBreakdown();

  return (
    <div className="p-5 rounded-2xl border border-surface-border bg-surface space-y-4">
      {/* Header */}
      <div className="space-y-0.5">
        <div className="flex items-center gap-2">
          <Layers className="size-4 text-primary" />
          <h3 className="text-sm font-bold font-heading text-foreground">
            {t('apikeys.chartEndpointUsage')}
          </h3>
        </div>
        <p className="text-xs text-foreground-muted">
          {t('apikeys.chartEndpointUsageDesc')}
        </p>
      </div>

      {/* Progress Bars List */}
      <div className="space-y-3.5 pt-1">
        {endpoints.map((item) => {
          let barBg = 'bg-primary';
          if (item.id === 'text') barBg = 'bg-accent';
          else if (item.id === 'images') barBg = 'bg-emerald-500';
          else if (item.id === 'voice') barBg = 'bg-amber-500';
          else if (item.id === 'translations') barBg = 'bg-sky-500';

          return (
            <div key={item.id} className="space-y-1.5">
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-foreground">
                    {item.name}
                  </span>
                  <code className="text-[10px] text-foreground-muted bg-surface-subtle px-1.5 py-0.5 rounded border border-surface-border font-mono">
                    {item.endpoint}
                  </code>
                </div>
                <div className="flex items-center gap-3 text-right">
                  <span className="text-[11px] text-foreground-muted">
                    {item.requests} calls
                  </span>
                  <span className="font-mono font-bold text-foreground min-w-[3rem]">
                    {item.percentage}%
                  </span>
                </div>
              </div>

              {/* Progress bar container */}
              <div className="h-2 w-full rounded-full bg-surface-border/50 overflow-hidden">
                <div
                  className={`h-full ${barBg} transition-all duration-500 rounded-full`}
                  style={{ width: `${Math.max(item.percentage, 2)}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
